import { useEffect, useId, useState } from 'react';
import { hasNamedTenantAction } from '../../product-policy.js';

const MANAGEMENT_ACTIONS = Object.freeze(['archive', 'restore', 'delete']);
const SAFE_REASON_CODE = /^[a-z][a-z0-9_]{0,95}$/;
const SAFE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SAFE_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const SAFE_DELETION_RESULTS = new Set(['campaign_deleted', 'campaign_already_deleted']);
const SAFE_RESTORE_STATES = new Set(['not_required', 'required', 'verified', 'failed']);
const MAX_DELETION_COUNT = 1_000_000;
const SAFE_DELETE_MODES = new Set(['hard', 'tombstone']);
const SAFE_DELETION_STATUSES = new Set(['in_progress', 'incomplete', 'completed']);
const SAFE_PROVIDER_OUTCOMES = new Set(['deleted', 'already_deleted', 'remaining']);
const SAFE_PROVIDER_ID = /^[0-9]{1,40}$/;
const MAX_RETRY_AFTER_SECONDS = 86_400;
const DELETE_BLOCKER_LABELS = Object.freeze({
  customer_value: 'published or provider-delivered customer value',
  publish_intents: 'publishing history',
  approvals: 'approval history',
  usage_events: 'recorded usage or cost history',
  workflow_jobs: 'workflow history',
  artifacts: 'campaign artifacts',
  snapshots: 'approved campaign snapshots',
  revision_requests: 'revision history',
  operation_requests: 'provider lifecycle history',
  cutover_probes: 'cutover safety evidence',
  funnels: 'a campaign funnel',
  funnel_version_assets: 'funnel assets',
});
const DELETE_ROLE_FORBIDDEN_MESSAGE = 'Only an owner or admin can delete a campaign.';
export const DELETE_HELPER_MESSAGE = 'Deleting removes this campaign, its ad sets and ads from Meta. Leads and spend history are kept.';
export const DELETE_DRAFT_HELPER_MESSAGE = 'Deleting removes this draft from BeyondLeads. Nothing was created on Meta.';
// OGC-455: the one statement of a campaign archived without reaching Meta.
// Details leads with it and the archived controls repeat it, so the two can
// never drift into different claims about what Meta is doing.
export const UNOBSERVABLE_ARCHIVE_NOTICE = Object.freeze({
  title: 'Managed in Meta only',
  body: 'BeyondLeads can’t reach this campaign — it was created under a Meta account that is no longer connected. If it is still running, it keeps spending until you pause it in Meta Ads Manager. Reconnect that account to manage it here again.',
});
// OGC-488: the same notice when Meta can no longer confirm the Meta login the
// campaign was published with. No reconnect can restore it, so it is not
// offered.
const ORIGINAL_UNPROVABLE_ARCHIVE_CAUSE = 'meta_campaign_credential_continuity_original_unprovable';
const ORIGINAL_UNPROVABLE_ARCHIVE_NOTICE = Object.freeze({
  title: UNOBSERVABLE_ARCHIVE_NOTICE.title,
  body: 'BeyondLeads can’t reach this campaign — Meta can no longer confirm the Meta login it was published with, and reconnecting won’t change that. If it is still running, it keeps spending until you pause it in Meta Ads Manager.',
});

export function unobservableArchiveNotice(archiveEvidence) {
  return archiveEvidence?.cause === ORIGINAL_UNPROVABLE_ARCHIVE_CAUSE
    ? ORIGINAL_UNPROVABLE_ARCHIVE_NOTICE
    : UNOBSERVABLE_ARCHIVE_NOTICE;
}

const DELETE_REASON_MESSAGES = Object.freeze({
  campaign_delete_role_forbidden: DELETE_ROLE_FORBIDDEN_MESSAGE,
  campaign_delete_not_archived: 'Archive this campaign before deleting it.',
  campaign_delete_provider_incomplete: 'Some Meta objects remain from the last delete attempt. Retry the delete.',
  campaign_delete_workflow_in_progress: 'A campaign workflow is still running. Wait for it to finish, then refresh before deleting.',
  campaign_delete_publish_in_progress: 'Publishing is still in progress. Wait for it to finish, then refresh before deleting.',
  campaign_delete_provider_control_in_progress: 'A Meta campaign update is still in progress. Wait for it to finish, then refresh before deleting.',
  campaign_delete_lead_ingress_in_progress: 'Lead delivery is still being processed. Wait for it to finish, then refresh before deleting.',
  campaign_delete_form_operation_in_progress: 'An Instant Form operation is still in progress. Wait for it to finish, then refresh before deleting.',
  campaign_delete_form_reservation_recovery_required: 'An interrupted Instant Form reservation requires recovery. Complete recovery, then refresh before deleting.',
  // OGC-455: the delete walk needs a credential we could not bind, so this
  // campaign can only be removed where it still exists.
  campaign_delete_provider_unreachable: 'This campaign was archived without reaching Meta, so it cannot be deleted from here. Delete it in Meta Ads Manager, or reconnect the Meta account that owns it and try again.',
});

function exactText(value) {
  return typeof value === 'string' && value.trim() === value ? value : '';
}

function invalidManagement() {
  const error = new Error('Campaign management response is invalid.');
  error.code = 'campaign_management_invalid';
  return error;
}

function safeNullableTimestamp(value) {
  if (value === null) return null;
  const normalized = exactText(value);
  return normalized && SAFE_TIMESTAMP.test(normalized) && Number.isFinite(Date.parse(normalized))
    ? normalized
    : undefined;
}

function safeReason(value) {
  const normalized = exactText(value);
  return SAFE_REASON_CODE.test(normalized) ? normalized : null;
}

function normalizeAction(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value) || typeof value.allowed !== 'boolean') {
    throw invalidManagement();
  }
  const reasonCode = value.reasonCode === null ? null : safeReason(value.reasonCode);
  if (reasonCode === null && value.reasonCode !== null) throw invalidManagement();
  if (value.allowed && reasonCode !== null) throw invalidManagement();
  return Object.freeze({ allowed: value.allowed, reasonCode });
}

function normalizeRestoreVerification(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw invalidManagement();
  const status = exactText(value.status).toLowerCase();
  const requiredAt = safeNullableTimestamp(value.requiredAt);
  const completedAt = safeNullableTimestamp(value.completedAt);
  if (!SAFE_RESTORE_STATES.has(status) || requiredAt === undefined || completedAt === undefined) {
    throw invalidManagement();
  }
  const coherent = status === 'not_required'
    ? requiredAt === null && completedAt === null
    : status === 'required'
      ? requiredAt !== null && completedAt === null
      : requiredAt !== null && completedAt !== null;
  if (!coherent) throw invalidManagement();
  return Object.freeze({ status, requiredAt, completedAt });
}

// OGC-455: only an archive that never reached Meta reports evidence, and the
// only shape accepted is the one the server sends.
function normalizeArchiveEvidence(value) {
  if (value === undefined || value === null) return null;
  if (
    typeof value !== 'object'
    || Array.isArray(value)
    || value.tier !== 'unobservable'
    || !(value.cause === null || (typeof value.cause === 'string' && safeReason(value.cause)))
  ) throw invalidManagement();
  return Object.freeze({ tier: 'unobservable', cause: value.cause || null });
}

export function normalizeCampaignManagementResponse(response, campaignId) {
  const value = response?.management;
  const expectedCampaignId = exactText(campaignId);
  const archivedAt = safeNullableTimestamp(value?.archivedAt);
  if (
    !value
    || typeof value !== 'object'
    || Array.isArray(value)
    || !expectedCampaignId
    || exactText(value.campaignId) !== expectedCampaignId
    || !Number.isSafeInteger(value.configurationRevision)
    || value.configurationRevision < 0
    || archivedAt === undefined
    || !value.actions
    || !value.deleteEligibility
    || !value.archiveWarning
  ) throw invalidManagement();

  const blockers = Array.isArray(value.deleteEligibility.blockers)
    && value.deleteEligibility.blockers.length <= 20
    && value.deleteEligibility.blockers.every((blocker) => (
      typeof blocker === 'string' && Object.hasOwn(DELETE_BLOCKER_LABELS, blocker)
    ))
    ? value.deleteEligibility.blockers.slice()
    : null;
  if (
    blockers === null
    || typeof value.deleteEligibility.eligible !== 'boolean'
    || typeof value.archiveWarning.required !== 'boolean'
  ) throw invalidManagement();

  const actions = Object.freeze(Object.fromEntries(
    MANAGEMENT_ACTIONS.map((action) => [action, normalizeAction(value.actions[action])]),
  ));
  const deleteEligibilityReason = value.deleteEligibility.reasonCode === null
    ? null
    : safeReason(value.deleteEligibility.reasonCode);
  const archiveWarningReason = safeReason(value.archiveWarning.reasonCode);
  if (deleteEligibilityReason === null && value.deleteEligibility.reasonCode !== null) {
    throw invalidManagement();
  }
  if (
    (archivedAt === null && (
      value.archiveWarning.required !== true
      || !['campaign_archive_pauses_delivery', 'campaign_archive_draft_only', 'campaign_archive_provider_delivery_unchanged'].includes(archiveWarningReason)
    ))
    || (archivedAt !== null && (
      value.archiveWarning.required !== false
      || value.archiveWarning.reasonCode !== null
    ))
  ) throw invalidManagement();
  // OGC-452: blockers no longer forbid deletion; they select the tombstone
  // mode. An eligible delete still never carries a reason code.
  if (
    (value.deleteEligibility.eligible && deleteEligibilityReason !== null)
    || (!value.deleteEligibility.eligible && actions.delete.allowed)
  ) throw invalidManagement();
  const mode = value.deleteEligibility.mode === undefined
    ? (blockers.length > 0 ? 'tombstone' : 'hard')
    : value.deleteEligibility.mode;
  if (!SAFE_DELETE_MODES.has(mode)) throw invalidManagement();
  const deletion = normalizeProviderDeletion(value.deletion);
  const archiveEvidence = normalizeArchiveEvidence(value.archiveEvidence);
  // Evidence only ever describes an archive that happened.
  if (archiveEvidence && archivedAt === null) throw invalidManagement();

  return Object.freeze({
    campaignId: expectedCampaignId,
    configurationRevision: value.configurationRevision,
    archivedAt,
    archivedBy: exactText(value.archivedBy) || null,
    archiveEvidence,
    restoreVerification: normalizeRestoreVerification(value.restoreVerification),
    actions,
    deleteEligibility: Object.freeze({
      eligible: value.deleteEligibility.eligible,
      reasonCode: deleteEligibilityReason,
      blockers: Object.freeze(blockers),
      mode,
    }),
    deletion,
    archiveWarning: Object.freeze({
      required: value.archiveWarning.required,
      reasonCode: archiveWarningReason,
    }),
  });
}

function normalizeProviderObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw invalidManagement();
  const id = exactText(value.id);
  const outcome = exactText(value.outcome);
  if (!SAFE_PROVIDER_ID.test(id) || !SAFE_PROVIDER_OUTCOMES.has(outcome)) throw invalidManagement();
  return Object.freeze({ id, outcome });
}

export function normalizeProviderEvidence(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'object' || Array.isArray(value)) throw invalidManagement();
  const campaign = value.campaign == null ? null : normalizeProviderObject(value.campaign);
  const adSets = Array.isArray(value.adSets) ? value.adSets.map(normalizeProviderObject) : [];
  const ads = Array.isArray(value.ads) ? value.ads.map(normalizeProviderObject) : [];
  if (adSets.length > 5 || ads.length > 50) throw invalidManagement();
  const objects = [...(campaign ? [campaign] : []), ...adSets, ...ads];
  const count = (outcome) => objects.filter((object) => object.outcome === outcome).length;
  return Object.freeze({
    campaign,
    adSets: Object.freeze(adSets),
    ads: Object.freeze(ads),
    counts: Object.freeze({
      deleted: count('deleted'),
      alreadyDeleted: count('already_deleted'),
      remaining: count('remaining'),
      total: objects.length,
    }),
  });
}

// OGC-452: the last provider-deletion attempt, when one exists.
export function normalizeProviderDeletion(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'object' || Array.isArray(value)) throw invalidManagement();
  const status = exactText(value.status);
  const retryAfterSeconds = value.retryAfterSeconds == null ? null : value.retryAfterSeconds;
  const retryNotBeforeAt = safeNullableTimestamp(value.retryNotBeforeAt ?? null);
  const updatedAt = safeNullableTimestamp(value.updatedAt ?? null);
  const cause = value.cause == null ? null : (typeof value.cause === 'string' ? value.cause.trim().slice(0, 200) : null);
  if (
    !SAFE_DELETION_STATUSES.has(status)
    || (retryAfterSeconds !== null && (!Number.isSafeInteger(retryAfterSeconds) || retryAfterSeconds < 1 || retryAfterSeconds > MAX_RETRY_AFTER_SECONDS))
    || retryNotBeforeAt === undefined
    || updatedAt === undefined
    || (value.cause != null && cause === null)
  ) throw invalidManagement();
  return Object.freeze({
    status,
    provider: normalizeProviderEvidence(value.provider) || normalizeProviderEvidence({ campaign: null, adSets: [], ads: [] }),
    lastErrorCode: value.lastErrorCode == null ? null : safeReason(value.lastErrorCode),
    cause,
    retryAfterSeconds,
    retryNotBeforeAt,
    attemptCount: Number.isSafeInteger(value.attemptCount) && value.attemptCount >= 1 ? value.attemptCount : 1,
    updatedAt,
  });
}

export function normalizeCampaignDeletionResponse(response, campaignId) {
  const value = response?.deletion;
  const expectedCampaignId = exactText(campaignId);
  const deletedAt = safeNullableTimestamp(value?.deletedAt);
  const counts = ['detachedLeadCaptureCount', 'detachedLeadIngressCount', 'retiredFormOperationCount'];
  if (
    !value
    || typeof value !== 'object'
    || Array.isArray(value)
    || exactText(value.campaignId) !== expectedCampaignId
    || !deletedAt
    || !SAFE_DELETION_RESULTS.has(exactText(value.resultCode))
    || typeof value.replayed !== 'boolean'
  ) throw invalidManagement();
  const mode = value.mode === undefined ? 'hard' : exactText(value.mode);
  if (!SAFE_DELETE_MODES.has(mode)) throw invalidManagement();
  if (mode === 'tombstone') {
    return Object.freeze({
      campaignId: expectedCampaignId,
      mode,
      deletedAt,
      resultCode: value.resultCode,
      replayed: value.replayed,
      provider: normalizeProviderEvidence(value.provider ?? null),
    });
  }
  if (
    !SAFE_UUID.test(exactText(value.receiptId))
    || !counts.every((field) => (
      Number.isSafeInteger(value[field]) && value[field] >= 0 && value[field] <= MAX_DELETION_COUNT
    ))
  ) throw invalidManagement();
  return Object.freeze({
    campaignId: expectedCampaignId,
    mode,
    receiptId: value.receiptId,
    deletedAt,
    resultCode: value.resultCode,
    replayed: value.replayed,
    ...Object.fromEntries(counts.map((field) => [field, value[field]])),
  });
}

// Minutes until a throttled delete may be retried; null when no wait applies.
export function campaignDeleteRetryWaitMinutes(deletion, nowMs = Date.now()) {
  const notBefore = Date.parse(String(deletion?.retryNotBeforeAt || ''));
  if (!Number.isFinite(notBefore) || notBefore <= nowMs) return null;
  return Math.max(1, Math.ceil((notBefore - nowMs) / 60_000));
}

// OGC-452: the wait re-evaluated when the instant passes, so a held "Retry
// delete" releases without a refresh (same shape as OGC-448's publish wait).
// A minute-granular label also ticks down once per minute while it holds.
export function useCampaignDeleteRetryWait(deletion) {
  const [, setTick] = useState(0);
  const untilMs = Date.parse(String(deletion?.status === 'incomplete' ? deletion?.retryNotBeforeAt || '' : ''));
  useEffect(() => {
    if (!Number.isFinite(untilMs)) return undefined;
    const remaining = untilMs - Date.now();
    if (remaining <= 0) return undefined;
    const timer = setTimeout(() => setTick((n) => n + 1), Math.min(remaining + 500, 60_000));
    return () => clearTimeout(timer);
  });
  return campaignDeleteRetryWaitMinutes(deletion);
}

export function campaignDeleteIncompleteMessage(deletion, nowMs = Date.now()) {
  if (deletion?.status !== 'incomplete') return '';
  const { counts } = deletion.provider;
  const done = counts.deleted + counts.alreadyDeleted;
  const wait = campaignDeleteRetryWaitMinutes(deletion, nowMs);
  const cause = deletion.cause || 'Meta refused the request.';
  const waitText = wait ? ` Try again in about ${wait} min.` : '';
  return `Delete incomplete. ${done} of ${counts.total} Meta objects were removed; ${cause}${waitText}`;
}

export function campaignManagementActionsForPolicy(management, productPolicy) {
  return Object.fromEntries(MANAGEMENT_ACTIONS.map((action) => [
    action,
    Boolean(
      management?.actions?.[action]?.allowed === true
      && hasNamedTenantAction(productPolicy, `campaign.${action}`),
    ),
  ]));
}

export function campaignManagementRetryKey(
  action,
  companyId,
  campaignId,
  createId = () => globalThis.crypto?.randomUUID?.(),
) {
  const segment = (value, fallback, length = 52) => (
    exactText(String(value || '')).replace(/[^A-Za-z0-9:_-]/g, '_').slice(0, length) || fallback
  );
  const normalizedAction = MANAGEMENT_ACTIONS.includes(action) ? action : 'manage';
  const nonce = segment(createId?.(), `${Date.now()}-${Math.random().toString(36).slice(2)}`, 64);
  return [
    'campaign-management',
    normalizedAction,
    segment(companyId, 'company'),
    segment(campaignId, 'campaign'),
    nonce,
  ].join(':').slice(0, 200);
}

export function campaignDeleteBlockerMessage(management) {
  const reasonCode = management?.deleteEligibility?.reasonCode;
  if (
    reasonCode === 'campaign_delete_provider_unreachable'
    && management?.archiveEvidence?.cause === ORIGINAL_UNPROVABLE_ARCHIVE_CAUSE
  ) {
    return 'This campaign was archived without reaching Meta, so it cannot be deleted from here. Delete it in Meta Ads Manager.';
  }
  if (Object.hasOwn(DELETE_REASON_MESSAGES, reasonCode)) {
    return DELETE_REASON_MESSAGES[reasonCode];
  }
  const blockerKey = typeof reasonCode === 'string'
    ? reasonCode.match(/^campaign_delete_blocked_([a-z][a-z0-9_]{0,79})$/)?.[1]
    : '';
  if (blockerKey && Object.hasOwn(DELETE_BLOCKER_LABELS, blockerKey)) {
    return `This campaign has ${DELETE_BLOCKER_LABELS[blockerKey]}, so it can be archived but not permanently deleted.`;
  }
  if (typeof reasonCode === 'string') {
    return 'Permanent deletion is unavailable for this campaign’s current lifecycle state.';
  }
  if (management?.actions?.delete?.reasonCode === 'role_forbidden') {
    return DELETE_ROLE_FORBIDDEN_MESSAGE;
  }
  return 'Permanent deletion is unavailable for this campaign’s current lifecycle state.';
}

export function campaignManagementActionBlocker(action, reasonCode) {
  const actionLabel = action === 'restore' ? 'restore' : 'archive';
  if (typeof reasonCode === 'string' && reasonCode.includes('role_forbidden')) {
    return `Your current company role cannot ${actionLabel} this campaign.`;
  }
  if (typeof reasonCode === 'string' && reasonCode.includes('already_')) {
    return action === 'restore'
      ? 'This campaign is already current in BeyondLeads.'
      : 'This campaign is already archived in BeyondLeads.';
  }
  if (
    typeof reasonCode === 'string'
    && /(workflow|publish|provider_control|lead_ingress|form_operation|reservation)/.test(reasonCode)
  ) {
    return `Wait for the current campaign operation to finish, then refresh before trying to ${actionLabel}.`;
  }
  return `${action === 'restore' ? 'Restore' : 'Archive'} is unavailable for this campaign’s current state.`;
}

export function CampaignManagementControls({
  busyAction = '',
  canonicalDetail = false,
  feedback = null,
  management,
  onArchive,
  onDelete,
  onDetails,
  onRetry,
  onRestore,
  productPolicy,
}) {
  const reactId = useId().replaceAll(':', '');
  if (!management) return null;
  const archived = Boolean(management.archivedAt);
  const restoreVerificationRequired = !archived
    && ['required', 'failed'].includes(management.restoreVerification?.status);
  const available = campaignManagementActionsForPolicy(management, productPolicy);
  const canArchive = hasNamedTenantAction(productPolicy, 'campaign.archive');
  const canRestore = hasNamedTenantAction(productPolicy, 'campaign.restore');
  const canDelete = hasNamedTenantAction(productPolicy, 'campaign.delete');
  const busy = Boolean(busyAction);
  const archiveBlocker = !available.archive
    ? campaignManagementActionBlocker('archive', management.actions?.archive?.reasonCode)
    : '';
  const restoreBlocker = !available.restore
    ? campaignManagementActionBlocker('restore', management.actions?.restore?.reasonCode)
    : '';
  const pendingBlocker = !busy
    ? ''
    : busyAction === 'stale'
      ? 'Refresh campaign controls before managing its BeyondLeads history.'
      : busyAction === 'refresh'
        ? 'Wait for the latest campaign controls to finish loading before managing its BeyondLeads history.'
        : 'Finish the current campaign update before managing its BeyondLeads history.';
  const archiveDescription = archiveBlocker || pendingBlocker;
  const restoreDescription = restoreBlocker || pendingBlocker;
  const deletion = management.deletion;
  const deleteIncomplete = deletion?.status === 'incomplete';
  const deleteWaitMinutes = useCampaignDeleteRetryWait(deletion);
  const deleteDescription = (!available.delete || !management.deleteEligibility.eligible)
    ? campaignDeleteBlockerMessage(management)
    : pendingBlocker;
  const deleteHelper = archived && canDelete && available.delete && management.deleteEligibility.eligible && !deleteDescription
    ? (management.deleteEligibility.mode === 'tombstone' ? DELETE_HELPER_MESSAGE : DELETE_DRAFT_HELPER_MESSAGE)
    : '';
  const deleteLabel = busyAction === 'delete'
    ? (management.deleteEligibility.mode === 'tombstone' ? 'Deleting in Meta…' : 'Deleting…')
    : deleteIncomplete
      ? (deleteWaitMinutes ? `Retry delete — wait ~${deleteWaitMinutes} min` : 'Retry delete')
      : 'Delete campaign';
  const archiveBlockerId = `campaign-archive-blocker-${reactId}`;
  const restoreBlockerId = `campaign-restore-blocker-${reactId}`;
  const deleteBlockerId = `campaign-delete-blocker-${reactId}`;

  return (
    <aside className={`bl-campaign-management ${archived ? 'is-archived' : ''}`} aria-label="Campaign management">
      <div className="bl-campaign-management-copy">
        <div className="bl-kicker">{archived
          ? 'Historical campaign'
          : restoreVerificationRequired ? 'Restore verification' : 'BeyondLeads management'}</div>
        <strong>{archived
          ? 'Archived in BeyondLeads'
          : restoreVerificationRequired ? 'Read-only until Meta is verified' : 'Campaign visibility'}</strong>
        <span>{archived
          ? (management.archiveEvidence?.tier === 'unobservable'
            ? 'This campaign was archived without reaching Meta, so nothing there was changed. Check its status in Meta Ads Manager. Performance and saved settings remain available here.'
            : 'Performance and saved settings remain available as historical evidence.')
          : restoreVerificationRequired
            ? canonicalDetail
              ? 'Refresh status here to reconcile the restored campaign with its current Meta state.'
              : 'Open Details to reconcile the restored campaign with its current Meta state.'
            : 'Archive stops published delivery before moving the campaign out of the current view, while preserving its history.'}</span>
      </div>
      {archived && management.archiveEvidence?.tier === 'unobservable' && (
        <div className="bl-campaign-archive-warning" role="note" data-campaign-archive-unobservable>
          <strong>{unobservableArchiveNotice(management.archiveEvidence).title}</strong>
          <span>{unobservableArchiveNotice(management.archiveEvidence).body}</span>
        </div>
      )}
      {!archived && !restoreVerificationRequired && (
        <div className="bl-campaign-archive-warning" role="note">
          <strong>Pause → confirm → archive</strong>
          <span>Published campaigns are archived only after Meta delivery is confirmed paused. Drafts archive directly. Restoring never resumes delivery.</span>
        </div>
      )}
      {feedback?.message && (
        <div
          className={`bl-campaign-management-feedback is-${feedback.status || 'status'}`}
          role={feedback.status === 'error' ? 'alert' : 'status'}
        >
          <span>{feedback.message}</span>
          {onRetry && ['warning', 'error'].includes(feedback.status) && (
            <button type="button" disabled={busy} onClick={onRetry}>Retry campaign controls</button>
          )}
        </div>
      )}
      <div className="bl-campaign-management-actions">
        {restoreVerificationRequired && onDetails && (
          <button type="button" onClick={onDetails}>Open Details</button>
        )}
        {!archived && !restoreVerificationRequired && canArchive && (
          <button
            type="button"
            aria-describedby={archiveDescription ? archiveBlockerId : undefined}
            disabled={busy || !available.archive}
            onClick={onArchive}
          >
            {busyAction === 'archive' ? 'Archiving…' : 'Archive campaign'}
          </button>
        )}
        {archived && canRestore && (
          <button
            type="button"
            aria-describedby={restoreDescription ? restoreBlockerId : undefined}
            disabled={busy || !available.restore}
            onClick={onRestore}
          >
            {busyAction === 'restore' ? 'Restoring…' : 'Restore campaign'}
          </button>
        )}
        {archived && canDelete && (
          <button
            type="button"
            className="is-danger"
            aria-describedby={deleteDescription || deleteHelper || deleteIncomplete ? deleteBlockerId : undefined}
            disabled={busy || !available.delete || !management.deleteEligibility.eligible || Boolean(deleteWaitMinutes)}
            onClick={onDelete}
          >
            {deleteLabel}
          </button>
        )}
      </div>
      {archived && canDelete && deleteIncomplete && (
        <div className="bl-campaign-management-feedback is-warning" role="alert" data-testid="campaign-delete-incomplete">
          <span>{campaignDeleteIncompleteMessage(deletion)}</span>
        </div>
      )}
      {!archived && !restoreVerificationRequired && canArchive && archiveDescription && (
        <p id={archiveBlockerId} className="bl-campaign-management-blocked">{archiveDescription}</p>
      )}
      {archived && canRestore && restoreDescription && (
        <p id={restoreBlockerId} className="bl-campaign-management-blocked">{restoreDescription}</p>
      )}
      {archived && canDelete && (deleteDescription || deleteHelper) && (
        <p id={deleteBlockerId} className={deleteDescription ? 'bl-campaign-management-blocked' : 'bl-campaign-management-helper'}>
          {deleteDescription || deleteHelper}
        </p>
      )}
    </aside>
  );
}
